"use client";
import { BuilderBlockProps, SortingObject } from "@/types";
import { FC, useContext, useEffect, useState } from "react";
import { IndexCardsContext } from "./IndexContextBlock";
import { Select } from "@/components/Select";
import { Dropdown } from "@/types/General/Dropdown";
import { createSortingMapping } from "@/helpers/sort";
import { getDataAutomationGroup } from "@/helpers";

type SortButtonBlockProps = {
  options: (SortingObject & {
    label: string;
    ariaLabel?: string;
    dld?: string;
    dlt?: string;
  })[];
  placeholder: string;
} & BuilderBlockProps;
export const SortButtonBlock: FC<SortButtonBlockProps> = (props) => {
  const options = props.options?.map((option) => ({
    label: option?.label,
    value: option?.label,
    ariaLabel: option?.ariaLabel,
    dld: option?.dld,
    dlt: option?.dlt,
  }));
  const [sortOption, setSortOption] = useState(
    props.placeholder ? { label: props.placeholder, value: "" } : options?.[0],
  );
  const sortingConfiguration = createSortingMapping(props.options);
  const { onSort, activeSortDescriptor } = useContext(IndexCardsContext);

  // Sync dropdown when activeSortDescriptor changes (e.g. from table column click)
  useEffect(() => {
    if (!activeSortDescriptor) {
      // Sort was cleared — reset to placeholder
      if (props.placeholder) {
        setSortOption({ label: props.placeholder, value: "" });
      }
      return;
    }

    const matchingIndex = props.options?.findIndex(
      (option) =>
        "field" in option &&
        "order" in option &&
        option.field === activeSortDescriptor.field &&
        option.order === activeSortDescriptor.order,
    );

    if (matchingIndex != null && matchingIndex >= 0) {
      const match = props.options[matchingIndex];
      setSortOption({ label: match.label, value: match.label });
    }
  }, [activeSortDescriptor, setSortOption, props.options, props.placeholder]);

  return (
    <div {...props.attributes}>
      <Select
        variant="rectangular"
        formControlContainerClassName="bg-background"
        options={options}
        value={sortOption}
        {...getDataAutomationGroup("dropdown")}
        onChange={(value: Dropdown) => {
          const index = options.findIndex(
            ({ value: optionValue }) => value.value === optionValue,
          );
          setSortOption(value);

          const option = props.options?.[index];
          const descriptor =
            option && "field" in option && "order" in option
              ? { field: option.field, order: option.order }
              : null;
          onSort(sortingConfiguration[index], descriptor);
        }}
      />
    </div>
  );
};
